import { useTranslation } from '@klv/hooks/useTranslation';

import algoliaIndexes from '../data';
import { VALID_ACADEMY_RESOURCE_TYPES, VALID_ACADEMY_RESOURCE_LANGUAGES } from '../constants';
import { formatFallbackItemLabel } from '../utils';

import type { TSearchIndexes } from '@klv/molecules/unified-search-results/types';
import type { ISearchIndex, IRefinementListItem } from '../types';

export default (defaultIndex: TSearchIndexes | '' = algoliaIndexes.academyIndex): ISearchIndex[] => {
  const { t } = useTranslation();

  const {
    academyIndex,
    communitIndex,
    developerIndex,
    helpCenterIndex,
    marketingSiteIndex,
  } = algoliaIndexes;

  return [
    {
      filters: '',
      label: t('pages_search_filters_academy'),
      refinements: [
        {
          attribute: 'types',
          label: t('pages_search_refinementsAccordionLabel_academy_type'),
          operator: 'or',
          transformItems: (items: IRefinementListItem[]) => {
            return items.filter(item => {
              if (VALID_ACADEMY_RESOURCE_TYPES.includes(item.label)) {
                if (item.label === 'Live training') {
                  item.label = t('pages_search_refinements_academy_type_liveTraining');
                }
                return item;
              }
            });
          },
        },
        {
          attribute: 'topics',
          label: 'Topic',
          operator: 'or',
          transformItems: (items: IRefinementListItem[]) => {
            return items.filter(item => {
              if ([
                'Account & billing',
                'Analytics',
                'Audience',
                'Advanced KDP',
                'Advanced Klaviyo Data Platform',
                'Campaigns',
                'Customer Hub',
                'Deliverability & compliance',
                'Developer',
                'Email',
                'Flows',
                'Integrations',
                'Marketing Analytics',
                'Push notifications',
                'Reviews',
                'Sign-up forms',
                'SMS',
              ].includes(item.label)) {
                return item;
              }
            });
          },
        },
        {
          attribute: 'level',
          label: 'Level',
          operator: 'or',
          transformItems: (items: IRefinementListItem[]) => {
            return items.filter(item => {
              if ([
                'Beginner',
                'Intermediate',
                'Advanced',
              ].includes(item.label)) {
                return item;
              }
            });
          },
        },
        {
          attribute: 'duration_buckets',
          label: t('pages_search_refinementsAccordionLabel_academy_duration'),
        },
        {
          attribute: 'language',
          label: t('pages_search_refinementsAccordionLabel_academy_language'),
          transformItems: (items: IRefinementListItem[]) => {
            return items.map((item) => {
              if (VALID_ACADEMY_RESOURCE_LANGUAGES.includes(item.label)) {
                item.label = t(`pages_search_refinements_academy_language_${item.label.toLowerCase()}`);
              }
              return item;
            });
          },
        },
        {
          attribute: 'region',
          label: t('pages_search_refinementsAccordionLabel_academy_location'),
          transformItems: (items: IRefinementListItem[]) => {
            return items.map((item) => {
              switch (item.label) {
                case 'APAC':
                  item.label = t('pages_search_refinements_academy_location_apac');
                  break;
                case 'EMEA':
                  item.label = t('pages_search_refinements_academy_location_emea');
                  break;
                case 'NAM':
                  item.label = t('pages_search_refinements_academy_location_nam');
                  break;
                default:
                  break;
              }

              return item;
            });
          },
        },
      ],
      value: academyIndex,
    },
    {
      filters: '',
      label: t('pages_search_filters_community'),
      refinements: [
        {
          operator: 'or',
          attribute: 'tags',
          label: 'Topic',
          transformItems: undefined,
        },
      ],
      value: communitIndex,
    },
    {
      filters: '',
      label: t('pages_search_filters_developers'),
      refinements: [
        {
          operator: 'or',
          attribute: 'tags',
          label: 'Guides',
          transformItems: (items: IRefinementListItem[]) => {
            return items.filter((item) => {
              if (item.label === 'API References' || item.label === 'Guides') return item;
            });
          }, 
        },
        {
          operator: 'or',
          attribute: 'tags',
          label: 'Topic',
          transformItems: (items: IRefinementListItem[]) => {
            return items.filter(item => {
              if (item.label === 'Track & Identify' ||
                  item.label === 'Metrics' ||
                  item.label === 'Profiles' ||
                  item.label === 'Lists & Segments' ||
                  item.label === 'Data Privacy' ||
                  item.label === 'Campaigns' ||
                  item.label === 'Templates') {
                return item;
              }
            });
          },
        },
      ],
      value: developerIndex,
    },
    {
      filters: `locale:${['en-US', 'en-GB'].includes(t('langCodes_locale')) ? 'en-US' : t('langCodes_locale')}`,
      label: t('pages_search_filters_helpCenter'),
      refinements: [
        {
          operator: 'or',
          attribute: 'topic',
          label: 'Topic',
        },
      ],
      value: helpCenterIndex,
    },
    {
      filters: `locale:${t('langCodes_locale')}`,
      label: t('pages_search_filters_klaviyoHome'),
      refinements: [
        {
          operator: 'or',
          attribute: 'topic',
          label: 'Topic',
          transformItems: (items: IRefinementListItem[]) => {
            return items.filter(item => {
              const fallback = formatFallbackItemLabel(item.label);
              const tLabel = t(`pages_search_refinements_klaviyoHome_topics_${item.label}`);
              item.label = tLabel || fallback;
              return item;
            });
          },
        },
        {
          operator: 'or',
          attribute: 'post_type',
          label: t('pages_search_refinements_klaviyoHome_postType'),
          transformItems: (items: IRefinementListItem[]) => {
            return items.filter(item => {
              item.label = t(`pages_search_refinements_klaviyoHome_postType_${item.label}`);
              return item;
            });
          },
        },
        {
          operator: 'or',
          attribute: 'category',
          label: t('pages_search_refinements_klaviyoHome_category'),
          transformItems: undefined,
        },
      ],
      value: marketingSiteIndex,
    },
  ].sort((a, b) => a.value == defaultIndex ? -1 : b.value == defaultIndex ? 1 : 0);
};
